/**
 * @file document-texts.ts
 * @description Общие тексты складских документов: почему нельзя провести,
 * чего не хватает строке и почему остаток без партии не разложить по ячейкам
 * (phase19.24 этап 1, шаг 1; тексты про партии — решение пользователя
 * 2026-09-20).
 *
 * Одна фраза на все документы. До этого приёмка и списание писали в шапке
 * «Добавьте хотя бы одну позицию», перемещение — «Добавьте хотя бы одну
 * строку», а на телефоне у приёмки и списания причины не было вовсе: кнопка
 * просто гасла (замечание ревью 2026-09-20). Теперь текст считает одна
 * функция, и шапка (`≥lg`) со sticky-баром (`<lg`) обязаны совпадать.
 *
 * Реестр вместо каскада `if`: новый документ = запись в `ROW_HINT` или ветка
 * по `kind`, а не свой текст в компоненте (OCP).
 */

import { pluralRu } from "../../lib/plural";
import { formatQtyValue } from "../../lib/picking-format";
import type { TransferUnplaceableItemDto } from "../../lib/api-transfers";

/** Складские документы с проведением. */
export type WmsDocumentKind = "receipt" | "writeoff" | "transfer";

/**
 * «L-240517 · до 17.05.2027» — партия со сроком одной строкой.
 *
 * Одна функция на приёмку, списание и перемещение: до неё каждый список
 * склеивал номер и срок сам, и приёмка читала только `item.batchNumber` —
 * техническая партия (её сервер заводит сам при приёмке без номера) приходит
 * в `item.lot`, и строка печатала «без партии», хотя партия есть (дефект QA
 * 2026-09-20). Технической пометки нет намеренно: кладовщику важен номер, а
 * не происхождение партии.
 *
 * phase19.31 (Б15): номера ещё нет (черновик приёмки — партию заведёт сервер
 * при проведении), а срок уже введён — срок не теряется: «присвоит система ·
 * до 08.10.2027». Раньше пустой номер возвращал только запасной текст, и
 * введённый срок пропадал с экрана.
 */
export function lotLabel(
  batchNumber: string | null | undefined,
  expiresAt: string | null | undefined,
  /** Чем заменить пустой номер: «без партии», «Автовыбор (FEFO)». */
  fallback = "без партии",
): string {
  const head = batchNumber || fallback;
  return expiresAt ? `${head} · до ${formatLotDate(expiresAt)}` : head;
}

/** Дата партии «дд.мм.гггг» — один формат на все документы склада. */
export function formatLotDate(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU");
}

/**
 * Почему проведение недоступно; `null` — документ готов.
 *
 * `blockedRows` — номера строк без «Куда»: только у перемещения, у остальных
 * документов список всегда пустой.
 */
export function documentCommitHint(
  kind: WmsDocumentKind,
  itemsCount: number,
  blockedRows: number[] = [],
): string | null {
  if (itemsCount === 0) return "Добавьте хотя бы одну строку";
  if (kind === "transfer" && blockedRows.length > 0) {
    return `Заполните «Куда» в строках: ${blockedRows.join(", ")}`;
  }
  return null;
}

/**
 * Чего не хватает строке — подпись под главной кнопкой панели. Порядок в
 * форме — порядок полей сверху вниз: кладовщик читает про ближайший
 * незаполненный шаг, а не про последний.
 */
export const ROW_HINT = {
  sku: "Выберите фасовку: скан, Enter или «Подбор»",
  quantity: "Укажите количество",
  lot: "Выберите партию",
  target: "Укажите, куда переместить",
} as const;

// ─── Лист размещения и остатки без партии (решение пользователя 2026-09-20) ──

/**
 * Хвост про W18 — один на все экраны: адрес хранения в системе принадлежит
 * партии, поэтому беспартийный остаток размещать некуда. Фраза повторяется в
 * полосе документа и в подсказке панели перемещения, и расходиться им нельзя.
 */
const W18_TAIL =
  "Партия появится у новых приёмок автоматически; старые остатки перейдут " +
  "в ячейки при переходе на учёт по ячейкам (W18).";

/** Сколько позиций перечисляем поимённо: длинный список полосу не читает. */
const UNPLACEABLE_PREVIEW = 3;

/**
 * Полоса листа размещения: какие позиции приёмки взять не удалось.
 *
 * «2 позиции приёмки без партии — разместить их нельзя: «Иван-чай 50 г ×3»,
 * «Мята 100 г ×1». Партия появится…». Тот же текст показывает пустое
 * состояние таблицы, когда строк в документе нет вовсе: «Добавьте партию
 * через панель справа» там врёт — добавлять нечего.
 */
export function unplaceableItemsText(items: TransferUnplaceableItemDto[]): string {
  const count = items.length;
  const shown = items
    .slice(0, UNPLACEABLE_PREVIEW)
    .map((item) => `«${item.name} ×${formatQtyValue(item.quantity)}»`)
    .join(", ");
  const rest = count - Math.min(count, UNPLACEABLE_PREVIEW);
  const tail = rest > 0 ? ` и ещё ${rest}` : "";
  const noun = pluralRu(count, ["позиция", "позиции", "позиций"]);
  const pronoun = count === 1 ? "её" : "их";
  return `${count} ${noun} приёмки без партии — разместить ${pronoun} нельзя: ${shown}${tail}. ${W18_TAIL}`;
}

/**
 * Тост «лист размещения не создан»: сервер не завёл документ, потому что
 * раскладывать нечего (контракт от 2026-09-20 — `data: null`).
 *
 * «Разместить нечего: 2 позиции без партии — «Иван-чай 50 г ×3», «Мята ×1»».
 * Перечисление обязательно: иначе кладовщик не понимает, что именно в приёмке
 * оформлено без партии и куда идти чинить.
 */
export function nothingToPlaceToast(items: TransferUnplaceableItemDto[]): string {
  if (items.length === 0) {
    return "Разместить нечего: партий без адреса в этой приёмке нет";
  }
  const count = items.length;
  const shown = items
    .slice(0, UNPLACEABLE_PREVIEW)
    .map((item) => `«${item.name} ×${formatQtyValue(item.quantity)}»`)
    .join(", ");
  const rest = count - Math.min(count, UNPLACEABLE_PREVIEW);
  const tail = rest > 0 ? ` и ещё ${rest}` : "";
  const noun = pluralRu(count, ["позиция", "позиции", "позиций"]);
  return `Разместить нечего: ${count} ${noun} без партии — ${shown}${tail}`;
}

/**
 * Почему «Разместить по ячейкам» недоступна у проведённой приёмки; `null` —
 * кнопка рабочая. Подпись под кнопкой по конвенции `DocumentHeaderActions`.
 *
 * `itemsWithoutLot` опционален (переходный период контракта): пока сервер
 * поле не отдаёт, кнопку не гасим — пусть лучше откроется пустой лист, чем
 * пропадёт единственная точка входа в размещение.
 */
export function placementBlockedHint(
  itemsWithoutLot: number | undefined,
  itemsCount: number,
): string | null {
  if (itemsWithoutLot === undefined || itemsCount === 0) return null;
  return itemsWithoutLot >= itemsCount
    ? "Все позиции без партии — размещать нечего"
    : null;
}

/**
 * Лист размещения, в котором размещать нечего: приёмку сделали с ячейкой в
 * каждой строке, все её партии уже с адресом, поэтому документ открылся без
 * строк (находка QA 2026-09-20).
 *
 * Это не ошибка и не пустой список «добавьте первую строку»: кладовщик должен
 * прочитать, что работа уже сделана, и что строку можно добавить руками —
 * чтобы переставить партию в другую ячейку. Один текст на три места: полосу
 * документа, пустую таблицу и подпись под заблокированной кнопкой.
 */
export function placementNothingToPlaceText(receiptNumber: string | null): string {
  const receipt = receiptNumber ? `приёмки ${receiptNumber}` : "приёмки";
  return (
    `Все партии ${receipt} уже с адресом ячейки — размещать нечего. ` +
    "Строку можно добавить вручную, чтобы переставить партию."
  );
}

/**
 * Почему нельзя провести лист размещения без строк. Отличается от «Добавьте
 * хотя бы одну строку»: добавлять нечего — партии уже разложены.
 */
export const NOTHING_TO_COMMIT_HINT = "Нет строк для проведения";

/**
 * Подсказка поля фасовки в панели перемещения, когда партий с остатком нет.
 * «Нет остатков» здесь читалось как «склад пуст», хотя товар на складе лежит —
 * просто без партии (замечание пользователя 2026-09-20).
 */
export const TRANSFER_NO_LOTS_HINT =
  "На складе нет партий с остатком — перемещать нечего. " +
  "Товар без партии переносится в ячейки после перехода на учёт по ячейкам (W18).";
